/**
 * 지도 관련 유틸리티 함수
 */

import userStorage from "./userStorage";

/**
 * 경도를 -180 ~ 180 범위로 정규화
 * @param lng 경도
 * @returns 정규화된 경도
 */
export const normalizeLongitude = (lng: number): number => {
  // 경도를 -180 ~ 180 범위로 순환
  while (lng > 180) lng -= 360;
  while (lng < -180) lng += 360;
  return lng;
};

/**
 * 폴리곤 내부에 점이 있는지 판단 (Ray Casting Algorithm)
 * @param point 확인할 점 { lat: number, lng: number }
 * @param polygon 폴리곤 좌표 배열
 * @returns 폴리곤 내부 여부
 */
const isPointInPolygon = (
  point: { lat: number; lng: number },
  polygon: { lat: number; lng: number }[]
): boolean => {
  let inside = false;
  const { lat, lng } = point;
  
  // 폴리곤의 각 엣지를 확인
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    
    // Ray casting algorithm
    const intersect = 
      ((yi > lat) !== (yj > lat)) &&
      (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    
    if (intersect) {
      inside = !inside;
    }
  }
  
  return inside;
};

/**
 * 한국 영역 내 좌표인지 확인
 * 폴리곤 경계를 기준으로 판단 (실제 그려진 폴리곤과 동일한 기준 사용)
 * @param lat 위도
 * @param lng 경도
 * @returns 한국 영역 내부 여부
 */
export const isKoreaRegion = (lat: number, lng: number): boolean => {
  // 경도 정규화 (-180 ~ 180)
  const normalizedLng = normalizeLongitude(lng);
  
  // 폴리곤 경계를 기준으로 내부/외부 판단
  const boundary = getKoreaRegionBoundary();
  return isPointInPolygon({ lat, lng: normalizedLng }, boundary);
};

/**
 * 오픈스트리트맵 사용 가능 여부 확인
 * 
 * 일반 사용자: URL이 mamolink.nongcall.com 또는 localhost:3000일 때만 OSM 사용
 * 관리자 (area="00000", grade=2): 항상 OSM 사용 가능 (지역 기반 전환)
 * 
 * @returns OSM 사용 가능 여부
 */
export const isOSMAvailable = (): boolean => {
  const user = userStorage.get();
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // 관리자 체크
  const isAdmin = user?.area === "00000" && user?.grade === 2;
  
  // OSM 허용 URL 체크 (일반 사용자용)
  const isOSMAllowedURL = 
    baseUrl === "https://mamolink.nongcall.com" || 
    baseUrl === "http://localhost:3000" ||
    window.location.origin === "http://localhost:3000"; // 개발 환경 대비

  // 관리자는 항상 OSM 사용 가능 (지역 기반 전환)
  // 일반 사용자는 특정 URL에서만 OSM 사용 가능
  return isAdmin || isOSMAllowedURL;
};

/**
 * 현재 사용할 지도 제공자 결정
 * 
 * 줌 레벨 기반 전환:
 *   - 카카오맵 레벨 13 이상(축소) → OSM으로 전환 (더 축소 가능)
 *   - 카카오맵 레벨 8 이하(확대) + 한국 → 카카오맵으로 복귀
 *   - Leaflet 줌 애니메이션 비활성화로 전환 시 에러 방지
 * 
 * 관리자 (area="00000", grade=2):
 *   - 국내 + 확대 → 카카오맵
 *   - 국외 또는 축소 → OSM
 * 
 * 일반 사용자:
 *   - OSM 허용 URL → OSM
 *   - 다른 URL → 카카오맵
 * 
 * @param lat 현재 지도 중심 위도
 * @param lng 현재 지도 중심 경도
 * @param level 현재 줌 레벨 (카카오맵 기준: 1=확대, 14=축소)
 * @returns 'kakao' | 'osm'
 */
export const getMapProvider = (
  lat: number,
  lng: number,
  level?: number
): "kakao" | "osm" => {
  const user = userStorage.get();
  const osmAvailable = isOSMAvailable();
  
  console.log(`🗺️ [getMapProvider] user=${user?.area}/${user?.grade}, osmAvailable=${osmAvailable}, lat=${lat}, lng=${lng}, level=${level}`);
  
  // OSM 사용 불가능한 경우 무조건 카카오맵
  if (!osmAvailable) {
    console.log(`🗺️ [getMapProvider] OSM 사용 불가 → 카카오맵`);
    return "kakao";
  }

  // 관리자인지 체크
  const isAdmin = user?.area === "00000" && user?.grade === 2;
  const inKorea = isKoreaRegion(lat, lng);
  
  console.log(`🗺️ [getMapProvider] isAdmin=${isAdmin}, inKorea=${inKorea}`);
  
  if (isAdmin) {
    // 관리자: 줌 레벨 기반 전환 (자연스러운 전환)
    if (level !== undefined) {
      // 레벨 13 이상(축소)이면 OSM으로 전환하여 더 축소 가능하게
      if (level >= 13) {
        return "osm";
      }
      // 레벨 8 이하(확대) + 한국이면 카카오맵으로 복귀
      if (level <= 8 && inKorea) {
        return "kakao";
      }
    }
    
    // 기본: 지역 기반 전환
    return inKorea ? "kakao" : "osm";
  } else {
    // 일반 사용자: 도메인에 따라 고정 (줌 레벨 무시)
    // OSM 허용 URL이면 OSM, 아니면 카카오맵
    console.log(`🗺️ [getMapProvider] 일반 사용자 → OSM`);
    return "osm";
  }
};

/**
 * 카카오맵 줌 레벨을 Leaflet 줌 레벨로 변환
 * 카카오맵: 1 (가장 확대) ~ 14 (가장 축소)
 * Leaflet: 1 (가장 축소) ~ 18 (가장 확대)
 * @param kakaoLevel 카카오맵 줌 레벨
 * @returns Leaflet 줌 레벨
 */
export const kakaoToLeafletZoom = (kakaoLevel: number): number => {
  // 카카오맵과 Leaflet의 줌 방향이 반대이므로 변환
  // OSM이 카카오맵보다 1칸 더 확대되도록 조정 (+1)
  // 카카오 1 -> Leaflet 19 (최대 확대)
  // 카카오 7 -> Leaflet 13
  // 카카오 14 -> Leaflet 6
  return Math.max(1, Math.min(18, 20 - kakaoLevel));
};

/**
 * Leaflet 줌 레벨을 카카오맵 줌 레벨로 변환
 * @param leafletZoom Leaflet 줌 레벨
 * @returns 카카오맵 줌 레벨
 */
export const leafletToKakaoZoom = (leafletZoom: number): number => {
  // 역변환 - kakaoToLeafletZoom의 역함수
  // Leaflet 19 -> 카카오 1
  // Leaflet 13 -> 카카오 7
  // Leaflet 6 -> 카카오 14
  return Math.max(1, Math.min(20, 20 - leafletZoom));
};

/**
 * 한국 영역 경계 폴리곤 좌표 생성
 * 카카오맵에서 OSM으로 전환되는 영역 범위를 표시하기 위한 경계선
 * @returns 경계 좌표 배열 { lat: number, lng: number }[]
 */
export const getKoreaRegionBoundary = (): { lat: number; lng: number }[] => {
  // 한국 영역 경계 좌표 (시계방향)
  // 위도별로 다른 동쪽 경계를 반영
  return [
    { lat: 33.0, lng: 124.5 },   // 남서쪽
    { lat: 33.0, lng: 127.0 },   // 남동쪽 (lat < 35.0일 때 동쪽 경계)
    { lat: 35.0, lng: 129.5 },   // 중부 동쪽 (35.0 <= lat < 37.0일 때 동쪽 경계)
    { lat: 37.0, lng: 132.0 },   // 북부 동쪽 (lat >= 37.0일 때 동쪽 경계)
    { lat: 38.5, lng: 132.0 },   // 북동쪽
    { lat: 38.5, lng: 124.5 },   // 북서쪽
    { lat: 33.0, lng: 124.5 },   // 다시 남서쪽으로 돌아가기 (폐곡선)
  ];
};
